import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Pencil, Plus, Receipt, Trash2 } from 'lucide-react';

import { errorMessage } from '@/api/client';
import { expensesApi } from '@/api/resources';
import {
  EmptyState,
  Loading,
  Money,
  MoneyInput,
  PageHeader,
  StatCard,
  SubmitButton,
} from '@/components/common';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { formatGs } from '@/lib/currency';
import { firstDayOfMonth, formatDate, todayApi } from '@/lib/dates';

const CATEGORY_STYLES = {
  Publicidad: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300',
  Mercadería: 'bg-purple-100 text-purple-800 dark:bg-purple-950 dark:text-purple-300',
  Alquiler: 'bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-300',
  Sueldos: 'bg-pink-100 text-pink-800 dark:bg-pink-950 dark:text-pink-300',
  Servicios: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-950 dark:text-cyan-300',
  Impuestos: 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300',
  Otros: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
};

const emptyForm = () => ({
  category: 'Otros',
  description: '',
  amount: 0,
  date: todayApi(),
  notes: '',
});

export default function Gastos() {
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [range, setRange] = useState({ from: firstDayOfMonth(), to: todayApi() });
  const [filterCategory, setFilterCategory] = useState('todas');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setExpenses(await expensesApi.list({ date_from: range.from, date_to: range.to }));
    } catch (error) {
      toast.error(errorMessage(error, 'No se pudieron cargar los gastos'));
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    expensesApi.categories().then(setCategories).catch(() => setCategories([]));
  }, []);

  const visible = useMemo(
    () =>
      filterCategory === 'todas'
        ? expenses
        : expenses.filter((e) => e.category === filterCategory),
    [expenses, filterCategory]
  );

  const totals = useMemo(() => {
    const byCategory = {};
    let total = 0;
    for (const expense of expenses) {
      byCategory[expense.category] = (byCategory[expense.category] || 0) + expense.amount;
      total += expense.amount;
    }
    return { byCategory, total };
  }, [expenses]);

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm());
    setDialogOpen(true);
  };

  const openEdit = (expense) => {
    setEditing(expense);
    setForm({
      category: expense.category,
      description: expense.description,
      amount: expense.amount,
      date: expense.date,
      notes: expense.notes || '',
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.description.trim()) {
      toast.error('Poné una descripción del gasto');
      return;
    }
    if (form.amount <= 0) {
      toast.error('El monto tiene que ser mayor a cero');
      return;
    }

    setSaving(true);
    const payload = {
      category: form.category,
      description: form.description.trim(),
      amount: form.amount,
      date: form.date,
      notes: form.notes.trim() || null,
    };

    try {
      if (editing) {
        await expensesApi.update(editing.id, payload);
        toast.success('Gasto actualizado');
      } else {
        await expensesApi.create(payload);
        toast.success('Gasto registrado y descontado de caja');
      }
      setDialogOpen(false);
      load();
    } catch (error) {
      toast.error(errorMessage(error, 'No se pudo guardar el gasto'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (expense) => {
    if (!window.confirm(`¿Eliminar el gasto "${expense.description}"?`)) return;
    try {
      await expensesApi.remove(expense.id);
      toast.success('Gasto eliminado y revertido en caja');
      load();
    } catch (error) {
      toast.error(errorMessage(error, 'No se pudo eliminar el gasto'));
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Gastos" subtitle="Cada gasto se descuenta automáticamente de la caja">
        <Button onClick={openNew} className="rounded-sm">
          <Plus size={18} className="mr-2" />
          Nuevo gasto
        </Button>
      </PageHeader>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard
          label="Total del período"
          value={formatGs(totals.total)}
          hint={`${expenses.length} gasto(s)`}
          icon={Receipt}
          tone="negative"
        />
        <StatCard label="Publicidad" value={formatGs(totals.byCategory.Publicidad || 0)} />
        <StatCard label="Mercadería" value={formatGs(totals.byCategory['Mercadería'] || 0)} />
        <StatCard
          label="Fijos"
          value={formatGs(
            (totals.byCategory.Alquiler || 0) +
              (totals.byCategory.Sueldos || 0) +
              (totals.byCategory.Servicios || 0)
          )}
          hint="Alquiler + sueldos + servicios"
        />
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-end gap-3 rounded-sm border border-border bg-card p-4">
        <div className="space-y-1">
          <Label className="text-xs">Desde</Label>
          <Input
            type="date"
            value={range.from}
            max={range.to}
            onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
            className="rounded-sm"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Hasta</Label>
          <Input
            type="date"
            value={range.to}
            min={range.from}
            onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
            className="rounded-sm"
          />
        </div>
        <div className="min-w-[160px] flex-1 space-y-1">
          <Label className="text-xs">Categoría</Label>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="rounded-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todas">Todas</SelectItem>
              {categories.map((category) => (
                <SelectItem key={category} value={category}>
                  {category}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <Loading />
      ) : visible.length === 0 ? (
        <EmptyState
          icon={Receipt}
          title="Sin gastos en este período"
          description="Cargá alquiler, sueldos, compras de mercadería o publicidad."
          action={
            <Button onClick={openNew} className="rounded-sm">
              <Plus size={18} className="mr-2" />
              Nuevo gasto
            </Button>
          }
        />
      ) : (
        <div className="divide-y divide-border rounded-sm border border-border bg-card">
          {visible.map((expense) => (
            <div key={expense.id} className="flex items-center gap-3 p-3">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                      CATEGORY_STYLES[expense.category] || CATEGORY_STYLES.Otros
                    }`}
                  >
                    {expense.category}
                  </span>
                  <span className="truncate font-medium">{expense.description}</span>
                </div>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {formatDate(expense.date)}
                  {expense.notes && ` · ${expense.notes}`}
                </p>
              </div>
              <Money value={expense.amount} className="shrink-0 font-bold" tone="negative" />
              <div className="flex shrink-0">
                <Button variant="ghost" size="icon" onClick={() => openEdit(expense)} aria-label="Editar">
                  <Pencil size={15} />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDelete(expense)}
                  aria-label="Eliminar"
                >
                  <Trash2 size={15} className="text-red-500" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar gasto' : 'Nuevo gasto'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Categoría *</Label>
              <Select
                value={form.category}
                onValueChange={(category) => setForm({ ...form, category })}
              >
                <SelectTrigger className="rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((category) => (
                    <SelectItem key={category} value={category}>
                      {category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="g-desc">Descripción *</Label>
              <Input
                id="g-desc"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Ej: Alquiler del local — agosto"
                required
                className="rounded-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="g-amount">Monto (Gs.) *</Label>
                <MoneyInput
                  id="g-amount"
                  value={form.amount}
                  onChange={(amount) => setForm({ ...form, amount })}
                  className="rounded-sm"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="g-date">Fecha *</Label>
                <Input
                  id="g-date"
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  required
                  className="rounded-sm"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="g-notes">Notas</Label>
              <Textarea
                id="g-notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
                className="rounded-sm"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                className="rounded-sm"
                onClick={() => setDialogOpen(false)}
              >
                Cancelar
              </Button>
              <SubmitButton loading={saving}>{editing ? 'Guardar' : 'Registrar'}</SubmitButton>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
